import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import SuperLayoutTitle from "@/components/layout/super-layout/super-layout-title";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { api } from "@/convex/_generated/api";
import { createTableHook, TableProps } from "@/hooks/useTableFactory";
import { Badge } from "@/components/ui/badge";
import { useToastMutation } from "@/hooks/useToastMutation";
import { useQuery } from "convex/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Users,
  Clock,
  CheckCircle,
  Share2,
  UserCheck,
  UserX,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const waitlistSearchSchema = z.object({
  page: z.number().optional().default(1),
  search: z.string().optional(),
  status: z.string().optional(),
});

export const Route = createFileRoute("/_super/super/waitlist")({
  validateSearch: waitlistSearchSchema,
  component: WaitlistPage,
});

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
};

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive"> =
  {
    pending: "secondary",
    approved: "default",
    rejected: "destructive",
  };

const INDUSTRY_LABELS: Record<string, string> = {
  retail: "Comercio minorista",
  wholesale: "Comercio mayorista",
  manufacturing: "Manufactura",
  services: "Servicios",
  food_beverage: "Alimentos y bebidas",
  healthcare: "Salud",
  education: "Educacion",
  technology: "Tecnologia",
  construction: "Construccion",
  agriculture: "Agricultura",
  other: "Otro",
};

type WaitlistData = Doc<"waitlist">;

const useWaitlistTable = createTableHook<WaitlistData>({
  tableName: "waitlist",
  query: api.system.waitlist.queries.getWaitlistOptimized,
  title: "Lista de Espera",
  icon: "ClipboardList",
  description: "Solicitudes de acceso y referidos",

  columns: (helpers) => [
    {
      key: "name",
      label: "Nombre",
      sortable: true,
      required: true,
      render: (value: string) => <div className="font-medium">{value}</div>,
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      render: (value: string) => (
        <div className="text-sm text-muted-foreground">{value}</div>
      ),
    },
    {
      key: "company",
      label: "Empresa",
      sortable: true,
      render: (value: string | undefined) => (
        <div className="text-sm text-muted-foreground">{value || "-"}</div>
      ),
    },
    {
      key: "industry",
      label: "Industria",
      render: (value: string | undefined) => (
        <div className="text-sm text-muted-foreground">
          {value ? (INDUSTRY_LABELS[value] ?? value) : "-"}
        </div>
      ),
    },
    {
      key: "companySize",
      label: "Tamano",
      render: (value: string | undefined) => (
        <div className="text-sm text-muted-foreground">
          {value ? `${value} emp.` : "-"}
        </div>
      ),
    },
    {
      key: "referralCode",
      label: "Codigo",
      render: (value: string) => (
        <span className="font-mono text-xs tracking-wider bg-muted px-1.5 py-0.5 rounded">
          {value}
        </span>
      ),
    },
    {
      key: "referralCount",
      label: "Referidos",
      sortable: true,
      render: (value: number) =>
        value > 0 ? (
          <Badge variant="outline" className="gap-1">
            <Share2 className="h-3 w-3" />
            {value}
          </Badge>
        ) : (
          <span className="text-sm text-muted-foreground">0</span>
        ),
    },
    {
      key: "referredBy",
      label: "Referido por",
      render: (value: string | undefined) =>
        value ? (
          <span className="font-mono text-xs tracking-wider">{value}</span>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        ),
    },
    {
      key: "status",
      label: "Estado",
      sortable: true,
      render: (value: string) => (
        <Badge variant={STATUS_VARIANTS[value] ?? "secondary"}>
          {STATUS_LABELS[value] ?? value}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      label: "Registro",
      sortable: true,
      render: (value: number) => (
        <div className="text-sm text-muted-foreground">
          {format(new Date(value), "dd MMM yyyy", { locale: es })}
        </div>
      ),
    },
  ],

  filters: () => [
    {
      id: "status",
      name: "Estado",
      icon: "SquareCheck",
      options: [
        { value: "all", label: "Todos" },
        { value: "pending", label: "Pendientes" },
        { value: "approved", label: "Aprobados" },
        { value: "rejected", label: "Rechazados" },
      ],
      defaultValue: "all",
    },
  ],

  customHooks: () => {
    const { execute: approveEntry } = useToastMutation(
      api.system.waitlist.mutations.approve,
      {
        loadingMessage: "Aprobando solicitud...",
        successMessage: "Solicitud aprobada. Email enviado al usuario.",
      },
    );

    const { execute: rejectEntry } = useToastMutation(
      api.system.waitlist.mutations.reject,
      {
        loadingMessage: "Rechazando solicitud...",
        successMessage: "Solicitud rechazada.",
      },
    );

    return { approveEntry, rejectEntry };
  },

  actions: (_helpers, customHooksData: Record<string, any>) => {
    const { approveEntry, rejectEntry } = customHooksData;

    return {
      customActions: [
        {
          label: "Aprobar",
          icon: UserCheck,
          onClick: (entry: WaitlistData) =>
            approveEntry({ waitlistId: entry._id }),
          condition: (entry: WaitlistData) => entry.status === "pending",
        },
        {
          label: "Rechazar",
          icon: UserX,
          onClick: (entry: WaitlistData) =>
            rejectEntry({ waitlistId: entry._id }),
          condition: (entry: WaitlistData) => entry.status === "pending",
        },
      ],
    };
  },

  createAction: "none",
});

function StatsCards() {
  const stats = useQuery(api.system.waitlist.queries.getStats);

  if (!stats) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-8 rounded-lg" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-16 mb-1" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: "Total registros",
      value: stats.total,
      description: "Solicitudes recibidas",
      icon: Users,
      iconColor: "text-blue-600",
      bgColor: "bg-blue-100 dark:bg-blue-950",
    },
    {
      title: "Pendientes",
      value: stats.pending,
      description: "Esperando aprobacion",
      icon: Clock,
      iconColor: "text-yellow-600",
      bgColor: "bg-yellow-100 dark:bg-yellow-950",
    },
    {
      title: "Aprobados",
      value: stats.approved,
      description: "Con acceso otorgado",
      icon: CheckCircle,
      iconColor: "text-green-600",
      bgColor: "bg-green-100 dark:bg-green-950",
    },
    {
      title: "Referidos totales",
      value: stats.totalReferrals,
      description: "Invitaciones exitosas",
      icon: Share2,
      iconColor: "text-purple-600",
      bgColor: "bg-purple-100 dark:bg-purple-950",
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {card.title}
              </CardTitle>
              <div className={`p-2 rounded-lg ${card.bgColor}`}>
                <Icon className={`h-4 w-4 ${card.iconColor}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
              <p className="text-xs text-muted-foreground">
                {card.description}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function WaitlistPage() {
  const searchParams = Route.useSearch();

  return (
    <div className="space-y-6">
      <SuperLayoutTitle
        title="Lista de Espera"
        subtitle="Gestiona las solicitudes de acceso y referidos"
      />
      <StatsCards />
      <WaitlistTable
        currentPage={searchParams.page}
        pageSize={20}
        basePath="/super/waitlist"
        searchParams={searchParams}
      />
    </div>
  );
}

function WaitlistTable({
  currentPage,
  pageSize,
  basePath,
  searchParams,
}: TableProps) {
  const table = useWaitlistTable({
    currentPage,
    pageSize,
    basePath,
    searchParams,
  });

  return table.render();
}
